import { useEffect, useState } from "react";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { Typography, AreaGraph } from "components/atoms";
import {
  FetchError,
  Header,
  FilterByLabel,
  PracticeDetailCard,
} from "components/molecules";
import { useAppSelector, useScreenSize } from "hooks";
import { MOBILE_WIDTH, TAB_LARGE_WIDTH, LSAT_TEST_TYPE, getPracticeGraphSeriesColor } from "utils/constants";
import { useGetTestResultsQuery } from "services/practice";
import { Spinner } from "components/atoms/Icon";
import { decodeToken } from "utils/functions/decodeToken";
import { ai_mcat_chat_status } from "state/reducers/layoutSlice";
import { applySameScoreVerticalOffset } from "utils/functions/applySameScoreVerticalOffset";
import "./index.scss";

interface IProps {
  theme: "light" | "dark";
}

export const PracticePage = ({ theme }: IProps) => {
  const [isPurpleChecked, setIsPurpleChecked] = useState(false);
  const [isGreenChecked, setIsGreenChecked] = useState(false);
  const [isBlueChecked, setIsBlueChecked] = useState(false);
  const [isChartRender, setIsChartRender] = useState(false);
  const [isMobileTestStart, setIsMobileTestStart] = useState(false);
  const [isGraphDataReFetch,setIsGraphDataReFetch] = useState(false);
  const {
    data: graphData,
    isLoading: graphIsLoading,
    isError: graphIsError,
  } = useGetTestResultsQuery();
  // const graphIsError = false;
  // const graphIsLoading = false;

  const payload = decodeToken();

  const { aiMCATChatDrawerStatus } = useAppSelector(
    (state: { layout: { aiMCATChatDrawerStatus: ai_mcat_chat_status } }) =>
      state.layout
  );

  const isLSATtest =
    payload &&
    payload.hasOwnProperty("testtype") &&
    payload.testtype === LSAT_TEST_TYPE;

  const dataPoints: any[] = [];
  const scoreGoalScore: any[] = [];
  const GuaranteeScore: any[] = [];
  const yAxisdata: number[] = [];
  const dataSeries: { label: string; data: any[]; borderColor: string }[] = [];

    const screen = useScreenSize();
    const location = useLocation();

  useEffect(() => {
    setIsChartRender(!isChartRender);
  }, [screen.width]);

  const formatData = (date: string): Date => {
    var utc = new Date(date);
    var offset = utc.getTimezoneOffset();
    var local = new Date(utc.getTime() + offset * 60000);
    return local;
  };

  if (graphData && graphData.Tests && graphData.Tests.length > 0) {
    const testsWithScores = graphData.Tests.filter(
      (t: any) => t.Scores && Array.isArray(t.Scores) && t.Scores.length > 0
    );

    if (testsWithScores.length > 1) {
      testsWithScores.forEach((test: any, idx: number) => {
        const sorted = [...test.Scores].sort(
          (a: any, b: any) =>
            (new Date(a.TestDate) as any) - (new Date(b.TestDate) as any)
        );
        const seriesData = sorted.map((s: any) => {
          const d = formatData(s.TestDate);
          return {
            x: d,
            y: s.Score,
            score: s.Score,
            testName: s.Name,
          };
        });
        seriesData.forEach((p: any) => yAxisdata.push(p.y));
        dataSeries.push({
          label: test.Name ?? test.TestName ?? `Series ${idx + 1}`,
          data: seriesData,
          borderColor: getPracticeGraphSeriesColor(theme, idx),
        });
      });
      applySameScoreVerticalOffset(dataSeries.map((s) => s.data));
      const allY = dataSeries.flatMap((s) =>
        s.data.map((p: any) => p.score ?? p.y)
      );
      if (allY.length > 0) {
        yAxisdata.length = 0;
        yAxisdata.push(...allY);
      }
      // Goal and guarantee from first test (same for all typically)
      const firstTest = testsWithScores[0];
      firstTest.Scores.forEach((s: any) => {
        scoreGoalScore.push({
          x: formatData(s.TestDate),
          y: firstTest.ScoreGoal,
        });
        GuaranteeScore.push({
          x: formatData(s.TestDate),
          y: firstTest.GuaranteeScore,
        });
      });
    } else {
      // Single series: keep existing flat list for backward compatibility
      const scoreArray: { score: any; testIndex: number }[] = [];
      graphData.Tests.forEach((test: any, testIndex: number) => {
        if (test.Scores && Array.isArray(test.Scores)) {
          test.Scores.forEach((item: any) => {
            scoreArray.push({ score: item, testIndex });
          });
        }
      });
      scoreArray.sort(
        (a, b) =>
          (new Date(a.score.TestDate) as any) -
          (new Date(b.score.TestDate) as any)
      );
      scoreArray.forEach(({ score, testIndex }) => {
        const test = graphData.Tests[testIndex];
        dataPoints.push({
          x: formatData(score.TestDate),
          y: score.Score,
          score: score.Score,
          testName: score.Name,
        });
        scoreGoalScore.push({
          x: formatData(score.TestDate),
          y: test.ScoreGoal,
        });
        GuaranteeScore.push({
          x: formatData(score.TestDate),
          y: test.GuaranteeScore,
        });
        yAxisdata.push(score.Score);
      });
      applySameScoreVerticalOffset([dataPoints]);
      if (dataPoints.length > 0) {
        yAxisdata.length = 0;
        dataPoints.forEach((p: any) => yAxisdata.push(p.score ?? p.y));
      }
    }
  }

  const hasScores = yAxisdata.length > 0;
  let minValue = hasScores ? Math.min(...yAxisdata) : 0;
  let maxValue = hasScores ? Math.max(...yAxisdata) : 0;
  let roundMin = Math.max(0, Math.round(minValue / 10) * 10 - 10);
  let roundMax = Math.round(maxValue / 10) * 10 + 10;

  return (
    <div className="practice-page-wrapper">
      <div className="header-section">
        <Header
          theme={theme}
          leftContentLabel="Practice"
          leftContentStyle="header-custom-style"
          leftContentTypographyStyle="left-content-typography-style"
          isTab={screen.width <= TAB_LARGE_WIDTH}
        />
      </div>

      {screen.width > MOBILE_WIDTH ? (
        <div className="topic-section">
          <Typography label="Test Results" variant="h3Bold" />
        </div>
      ) : null}

      <div
        className={classNames(
          "chart-section",
          graphData &&
            (graphData.ShowGuaranteeScore ||
              graphData.ShowGoal ||
              graphData.ShowProjectedScore) &&
            "flex-class"
        )}
      >
        {screen.width < MOBILE_WIDTH ? (
          <div className="mobile-topic-section">
            <Typography label="Test Results" variant="h6Bold" />
          </div>
        ) : null}

        {graphIsLoading ? (
          <Spinner theme={theme} />
        ) : graphIsError ? (
          <div className={classNames("error-text-container")}>
            <FetchError content="Oops! failed to load resources." />
          </div>
        ) : (
          <>
            {isChartRender && graphData && (
              <div className="chart-wrapper">
                <AreaGraph
                  theme={theme}
                  blueFilter={isBlueChecked}
                  greenFilter={isGreenChecked}
                  purpleFilter={isPurpleChecked}
                  maxScore={graphData.MaxScore}
                  isBeginningZero={false}
                  guaranteeScoreData={GuaranteeScore}
                  scoreGoalData={scoreGoalScore}
                  dataPoints={dataPoints}
                  dataSeries={dataSeries.length > 0 ? dataSeries : undefined}
                  roundMin={!hasScores ? 0 : roundMin === 0 ? 0 : roundMin}
                  roundMax={roundMax}
                />
              </div>
            )}
            {!isChartRender && graphData && (
              <div className="chart-wrapper">
                <AreaGraph
                  theme={theme}
                  blueFilter={isBlueChecked}
                  greenFilter={isGreenChecked}
                  purpleFilter={isPurpleChecked}
                  maxScore={graphData.MaxScore}
                  isBeginningZero={graphData.MinScore === 0}
                  guaranteeScoreData={GuaranteeScore}
                  scoreGoalData={scoreGoalScore}
                  dataPoints={dataPoints}
                  dataSeries={dataSeries.length > 0 ? dataSeries : undefined}
                  roundMin={!hasScores ? 0 : roundMin === 0 ? 0 : roundMin}
                  roundMax={roundMax}
                />
              </div>
            )}
            <div className="label-section-wrapper">
              {graphData &&
              (graphData.ShowGuaranteeScore ||
                graphData.ShowGoal ||
                graphData.ShowProjectedScore) ? (
                <div className="filter-by-typography">
                  <Typography label="Filter by:" variant="h7Book" />
                </div>
              ) : null}
              {graphData && graphData.ShowGuaranteeScore ? (
                <FilterByLabel
                  label="Guarantee Score"
                  color="green"
                  styles={
                    screen.width < MOBILE_WIDTH
                      ? "label-mobile-custom-style"
                      : ""
                  }
                  isChecked={isGreenChecked}
                  setIsChecked={setIsGreenChecked}
                />
              ) : null}
              {graphData && graphData.ShowGoal ? (
                <FilterByLabel
                  label="Goal"
                  color="blue"
                  styles={
                    screen.width < MOBILE_WIDTH
                      ? "label-mobile-custom-style"
                      : ""
                  }
                  isChecked={isBlueChecked}
                  setIsChecked={setIsBlueChecked}
                />
              ) : null}
              {graphData && graphData.ShowProjectedScore ? (
                <FilterByLabel
                  label="Projected Score"
                  color="purple"
                  styles={
                    screen.width < MOBILE_WIDTH
                      ? "label-mobile-custom-style"
                      : ""
                  }
                  isChecked={isPurpleChecked}
                  setIsChecked={setIsPurpleChecked}
                />
              ) : null}
            </div>
          </>
        )}
      </div>
      <div className={classNames("bottom-detail-card-section",aiMCATChatDrawerStatus === "closed"
          ? "bottom-detail-card-section-ai-chat-drawer-closed"
          : "")}>
        <PracticeDetailCard
          theme={theme}
          title={
            screen.width < 427
              ? "Practice Tests"
              : "Practice Tests & Score Reports"
          }
          isTabShow={true}
          description={
            isLSATtest
              ? "Take the suggested practice tests on schedule then analyze your results using the score reports and explanations to improve your score!"
              : "Take more practice tests and check your score to see what areas need improvement. Taking practice tests and viewing your score report have a major impact on your score improvement"
          }
        />
        {/* <FlashCard
          title="Flashcards"
          description="Create your own flashcard decks to study anywhere"
        /> */}
      </div>
    </div>
  );
};
